import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Drawer,
  DrawerHeader,
  DrawerPages,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  Icon,
  Sheet,
  SheetClose,
  SheetPanel,
  SheetPopup,
  Separator,
  SheetTitle,
  TooltipSideProvider,
  useIsMobile,
  useOptionalAutoFocus,
  cn,
} from "@salestrak/ui";

import { FieldTypeTitle } from "./add-field-page";
import {
  BROWSE_STEP,
  FieldsPageStepContext,
  FieldsSearchInputContext,
  OPTIONS_PANEL_TRIGGER_ATTR,
  OptionsPanelHeaderRungContext,
  type FieldsPageStep,
  type FieldsPageStepValue,
} from "./options-panel-slots";
import { FieldsManagerAction } from "#app/components/custom-fields/fields-manager-action";
import { useAnchorInsets } from "#app/lib/hooks/use-anchor-insets";
import { useCloseOnNavigate } from "#app/lib/hooks/use-close-on-navigate";
import type { PanelLeaveGuard } from "#app/lib/views/view-context";

const PANEL_WIDTH = "w-[359px] max-w-none before:hidden";
/** The card frame: a widget's own Customize view, pinned over the card by
 *  `widgetCardDomId`. Narrower — a card is a column, not a pane — and both
 *  right corners follow the card's `lg` radius, since the panel spans the
 *  card's full height, header included. */
const CARD_PANEL_WIDTH = "w-[320px] max-w-none before:hidden";

/**
 * Where an outside press is NOT a press on the page, so the desktop panel
 * stays: the panel's own trigger (its handler decides — a toggle closes, an
 * opener switches page); any backdrop, ours or Base UI's own — that press
 * closes the layer above the panel, not the panel; and the inside of any
 * other popup — a menu, a popover, a dialog raised over the panel. A sheet
 * is the one popup that counts as the page: the project drawer is where the
 * reader works, not a layer over it.
 */
const OUTSIDE_PRESS_IGNORED = [
  `[${OPTIONS_PANEL_TRIGGER_ATTR}]`,
  '[data-slot$="-backdrop"]',
  '[data-base-ui-inert][role="presentation"]',
  '[data-slot$="-popup"]:not([data-slot="sheet-popup"])',
  // A hover card a page row opened is a layer over the page, like a popup.
  '[data-slot="preview-card-content"]',
].join(", ");

/** How far a mouse press may travel and still be a press. Past it, the
 *  press was a drag (a map pan, a column resize), and the browser's `click`
 *  at its end is not a press on the page. */
const DRAG_SLOP_PX = 5;

/** A close Base UI raises for a TOUCH that moved: a scroll, not a tap. A
 *  tap closes through the `mousedown` the browser sends after it. */
function isTouchGesture(event: Event | undefined): boolean {
  return event?.type === "touchmove" || event?.type === "touchend";
}

/** The shape Base UI hands `onOpenChange`, for the sheet and the drawer
 *  alike: why, from what, and a way to refuse. */
type PanelOpenChangeDetails = {
  reason: string;
  event?: Event | undefined;
  cancel: () => void;
};

/** What a confirmed discard does: close the panel, go back a step, or let
 *  the host's page change run. */
type PendingDiscard = "close" | "back" | "page";

export type OptionsPanelShellProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  anchorId?: string | undefined;
  onBack?: (() => void) | undefined;
  backLabel?: string | undefined;
  /** The Fields page: the panel puts the caret in its search on arrival, and
   *  the shell owns the page's steps — the picker and the create form —
   *  drawing the header each one needs. The picker opens with a header
   *  rung. */
  fieldsPage?: boolean | undefined;
  /** The page supplies a header rung of its own — a search well, a tab
   *  strip — through `OptionsPanelHeaderRung`. It ends in a hairline, so
   *  the shell draws none under the title: two rules a rung apart read as a
   *  double edge. */
  headerRung?: boolean | undefined;
  /** What the panel pins inside: the view pane (default — one rounded corner,
   *  the pane's bottom-right, since the tabs sit above it) or a widget CARD
   *  (320px; both right corners at the card's radius). */
  frame?: "pane" | "card" | undefined;
  /** Where the host asks before it changes the open panel's page (see
   *  `PanelLeaveGuard`), so a page change from outside the panel cannot drop
   *  a typed draft without the question. */
  onLeaveGuardChange?: ((guard: PanelLeaveGuard | null) => void) | undefined;
  children: React.ReactNode;
};

/**
 * The Fields page's door to the Custom Field Manager, beside the close. The
 * manager is where fields are edited in bulk; the page's own flow creates
 * one and shows it. `custom_fields.manage` only — without it the manager
 * has nothing to offer.
 */
/** Responsive chrome shared by controlled Customize view panels. */
export function OptionsPanelShell({
  open,
  onOpenChange,
  title,
  anchorId,
  onBack,
  backLabel = "Back to Customize view",
  fieldsPage = false,
  headerRung = false,
  frame = "pane",
  onLeaveGuardChange,
  children,
}: OptionsPanelShellProps) {
  const isMobile = useIsMobile();

  // --- The create form's draft. The Fields page's create form reports
  // whether it holds anything typed; while it does, every way out — a press
  // beside the panel, Escape, the close button, Back, the host closing the
  // panel from its own trigger — asks first. The form's own Cancel is an
  // explicit discard and stays immediate. ---
  const draftDirtyRef = useRef(false);
  const reportDirty = useCallback((dirty: boolean) => {
    draftDirtyRef.current = dirty;
  }, []);
  const [pendingDiscard, setPendingDiscard] = useState<PendingDiscard | null>(
    null,
  );
  // The host closed the panel from outside the sheet (its toolbar trigger)
  // while a draft was typed: the sheet holds itself open over the host's
  // `false` until the reader answers, then follows it or asks it to reopen.
  const [held, setHeld] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);
  let holding = held;
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open && draftDirtyRef.current) {
      holding = true;
      setHeld(true);
      setPendingDiscard("close");
    } else if (held) {
      holding = false;
      setHeld(false);
    }
  }
  const sheetOpen = open || holding;

  // A page change the host asked for while a draft was typed, kept until the
  // reader answers.
  const pendingPageRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!onLeaveGuardChange) return undefined;
    onLeaveGuardChange((proceed) => {
      if (!draftDirtyRef.current) return true;
      pendingPageRef.current = proceed;
      setPendingDiscard("page");
      return false;
    });
    return () => onLeaveGuardChange(null);
  }, [onLeaveGuardChange]);

  // Where the last press on the page went down, so a `click` at the end of
  // a drag is told from a press.
  const pressStartRef = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const record = (event: PointerEvent) => {
      pressStartRef.current = { x: event.clientX, y: event.clientY };
    };
    document.addEventListener("pointerdown", record, true);
    return () => document.removeEventListener("pointerdown", record, true);
  }, [sheetOpen]);

  // A close nothing should hold up: the route changed under the panel, or
  // the card it was pinned to scrolled away. The draft goes with it.
  const closeNow = useCallback(() => {
    draftDirtyRef.current = false;
    pendingPageRef.current = null;
    setPendingDiscard(null);
    onOpenChange(false);
  }, [onOpenChange]);
  useCloseOnNavigate(open, closeNow);
  const anchorInsets = useAnchorInsets(anchorId, open && !isMobile);
  // A panel pinned over a scrolling anchor (a widget card) closes once the
  // card has scrolled out of view: a drawer over a card that is not there
  // has nothing to edit, and its header would otherwise stand as a sliver
  // over whatever scrolled in under it.
  const anchorHidden = Boolean(anchorInsets?.hidden);
  // An automatic close SNAPS: the exit slide-and-fade the sheet plays for a
  // press on Close would play here over whatever cards scrolled in under
  // the panel, since nothing clips the sheet to its card. The flag zeroes
  // the transition for that one close and lifts again on the next open.
  const [snapClose, setSnapClose] = useState(false);
  useEffect(() => {
    if (open && anchorHidden) {
      setSnapClose(true);
      closeNow();
    }
  }, [anchorHidden, closeNow, open]);
  useEffect(() => {
    if (open) setSnapClose(false);
  }, [open]);
  const fieldsSearchRef = useRef<HTMLInputElement | null>(null);

  // The Fields page's step. It starts over each time the page is reached —
  // a close, or a trip back to the main page, retires a half-taken flow. The
  // reset lands in render, not an effect, so no frame shows the old step.
  const [fieldsStep, setFieldsStep] = useState<FieldsPageStep>(BROWSE_STEP);
  const fieldsActive = sheetOpen && fieldsPage;
  const [prevFieldsActive, setPrevFieldsActive] = useState(fieldsActive);
  if (fieldsActive !== prevFieldsActive) {
    setPrevFieldsActive(fieldsActive);
    if (fieldsActive) setFieldsStep(BROWSE_STEP);
  }
  const step: FieldsPageStep = fieldsPage ? fieldsStep : BROWSE_STEP;
  const stepValue = useMemo<FieldsPageStepValue>(
    () => ({ step: fieldsStep, setStep: setFieldsStep, reportDirty }),
    [fieldsStep, reportDirty],
  );

  // --- Every way out runs through here. A sheet-born close (a press beside
  // the panel, Escape, the close button) is refused while a draft is typed
  // and the question is asked instead; the host's own closes are held above.
  // Outside presses that are not presses on the page are refused outright,
  // and so is focus leaving the panel: a side panel over a live page is left
  // by a press or a key, not by where the caret went. ---
  const guardClose = useCallback(
    (next: boolean, details: PanelOpenChangeDetails, onPage: boolean) => {
      if (next) {
        onOpenChange(true);
        return;
      }
      if (onPage) {
        if (details.reason === "focus-out") {
          details.cancel();
          return;
        }
        if (details.reason === "outside-press") {
          const event = details.event;
          const target = event?.target;
          if (target instanceof Element && target.closest(OUTSIDE_PRESS_IGNORED)) {
            details.cancel();
            return;
          }
          // A scroll on a touch screen, or a drag with a mouse, is work on
          // the page the panel is shaping; only a press leaves it.
          if (isTouchGesture(event)) {
            details.cancel();
            return;
          }
          const start = pressStartRef.current;
          if (event instanceof MouseEvent && event.type === "click" && start) {
            const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
            if (moved > DRAG_SLOP_PX) {
              details.cancel();
              return;
            }
          }
        }
      }
      if (draftDirtyRef.current) {
        details.cancel();
        setPendingDiscard("close");
        return;
      }
      onOpenChange(false);
    },
    [onOpenChange],
  );
  const handleSheetOpenChange = useCallback(
    (next: boolean, details: PanelOpenChangeDetails) =>
      guardClose(next, details, true),
    [guardClose],
  );
  const handleDrawerOpenChange = useCallback(
    (next: boolean, details: PanelOpenChangeDetails) =>
      guardClose(next, details, false),
    [guardClose],
  );
  const confirmDiscard = useCallback(() => {
    const scope = pendingDiscard;
    setPendingDiscard(null);
    draftDirtyRef.current = false;
    if (scope === "page") {
      const proceed = pendingPageRef.current;
      pendingPageRef.current = null;
      proceed?.();
    } else if (scope === "back") {
      setFieldsStep({ kind: "browse", tab: "create" });
    } else if (held) {
      // The host already closed; the sheet follows it now.
      setHeld(false);
    } else {
      onOpenChange(false);
    }
  }, [held, onOpenChange, pendingDiscard]);
  const keepEditing = useCallback(() => {
    setPendingDiscard(null);
    pendingPageRef.current = null;
    if (held) {
      setHeld(false);
      onOpenChange(true);
    }
  }, [held, onOpenChange]);

  // What the header says for the step: the picker wears the page's title
  // and the host's Back; the create form is titled by its type — the
  // switcher IS the title — and backs out to the picker's Create new tab.
  // The manager door rides the picker; the form is one field's business.
  let headerTitle: React.ReactNode = title;
  let headerBack = onBack;
  let headerBackLabel = backLabel;
  if (step.kind === "create") {
    headerTitle = (
      <FieldTypeTitle
        type={step.type}
        onTypeChange={(type) => setFieldsStep({ kind: "create", type })}
      />
    );
    headerBack = () => {
      if (draftDirtyRef.current) setPendingDiscard("back");
      else setFieldsStep({ kind: "browse", tab: "create" });
    };
    headerBackLabel = "Back to Fields";
  }
  const headerActions =
    fieldsPage && step.kind === "browse" ? (
      <TooltipSideProvider side="left">
        <FieldsManagerAction />
      </TooltipSideProvider>
    ) : null;
  // Each step is its own page: it opens at its top, and the body's rung
  // (or its absence — the create form has none) is the step's own.
  const pageKey = fieldsPage ? `${title}:${step.kind}` : title;
  const hasHeaderRung = (fieldsPage && step.kind !== "create") || headerRung;
  // The page's header rung lands here, above the scroll region, so the list
  // scrolls under it. The host is a Box the body portals into; the rung
  // unmounts with the body that owns it, so a page switch never strands one.
  const [rungHost, setRungHost] = useState<HTMLDivElement | null>(null);
  const rungPortal = useMemo(
    () => ({ host: rungHost, setHost: setRungHost }),
    [rungHost],
  );

  // The search mounts one commit after its host does (the portal waits for
  // the node), so the host is a dependency: the caret lands once the input
  // exists.
  // The step is a dependency too: the picker mounts a search of its own and
  // the caret moves to it; the create form seats focus in its name field.
  // Typing is optional here — the list is the page — so the caret lands only
  // where a hardware keyboard is implied: on a coarse pointer (every iPad,
  // whatever its layout tier) the field waits for its own tap rather than
  // raising the keyboard over the rows.
  const seatSearchCaret = useOptionalAutoFocus();
  useEffect(() => {
    if (seatSearchCaret && open && fieldsPage) fieldsSearchRef.current?.focus();
  }, [fieldsPage, open, rungHost, seatSearchCaret, step]);

  // The question a draft asks before it is thrown away. A modal above the
  // panel, so a press on its scrim keeps editing rather than closing both.
  const discardDialog = (
    <AlertDialog
      open={pendingDiscard !== null}
      onOpenChange={(next) => {
        if (!next) keepEditing();
      }}
    >
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription>
            This field hasn&rsquo;t been created yet. Leaving now will discard
            it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            Keep editing
          </AlertDialogClose>
          <Button color="destructive" onClick={confirmDiscard}>
            Discard changes
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );

  // The panel is pinned to the end edge, so a tooltip that names no side
  // opens LEFT — beside its row, not over the row above it. A popup that
  // names its own side still wins. The rung's content portals out of the
  // body but is rendered inside it, so it inherits the same default.
  const body = (
    <TooltipSideProvider side="left">
      <OptionsPanelHeaderRungContext.Provider value={rungPortal}>
        {fieldsPage ? (
          <FieldsPageStepContext.Provider value={stepValue}>
            <FieldsSearchInputContext.Provider value={fieldsSearchRef}>
              {children}
            </FieldsSearchInputContext.Provider>
          </FieldsPageStepContext.Provider>
        ) : (
          children
        )}
      </OptionsPanelHeaderRungContext.Provider>
    </TooltipSideProvider>
  );
  const rungSlot = hasHeaderRung ? (
    <Box ref={setRungHost} direction="column" shrink={false} />
  ) : null;

  const backButton = headerBack ? (
    <Box direction="row" align="center" marginLeftNegative="xs" shrink={false}>
      <Button
        aria-label={headerBackLabel}
        onClick={headerBack}
        size="icon-xs"
        variant="ghost"
      >
        <Icon name="arrow-left" />
      </Button>
    </Box>
  ) : null;

  if (isMobile) {
    return (
      <>
        <Drawer
          open={sheetOpen}
          onOpenChange={handleDrawerOpenChange}
          size="full"
        >
          {/* Page fill, matching the desktop panel and the project drawer. */}
          <DrawerPopup showBar surface="background">
            {/* The panel's own pages swap in place under one header (the
                chevron above goes back a page). The stack is for a page's
                ROWS that drill one level further — the Filters page's value
                lists — which push over the header and panel below. */}
            <DrawerPages>
              <DrawerHeader>
                <Box direction="row" align="center" justify="between" gap="sm">
                  <Box direction="row" align="center" gap="xs" shrink fill="flex">
                    {backButton}
                    <DrawerTitle>{headerTitle}</DrawerTitle>
                  </Box>
                  {headerActions}
                </Box>
              </DrawerHeader>
              {rungSlot}
              {/* The rung's own hairline separates it from the list; a bare
                  title row keeps the 12px lead-in. */}
              <DrawerPanel
                key={pageKey}
                className={hasHeaderRung ? "px-0 pt-0" : "px-0 pt-3"}
              >
                {body}
              </DrawerPanel>
            </DrawerPages>
          </DrawerPopup>
        </Drawer>
        {discardDialog}
      </>
    );
  }

  return (
    <>
      {/* A side panel over a live page: the page behind stays undimmed
          (`showBackdrop={false}`) and interactive (`modal={false}`), and a
          press on it closes the panel — the reader has moved on. A press that
          is not on the page (the panel's trigger, a popup or modal raised
          over the panel, their scrims) leaves it where it is; see
          OUTSIDE_PRESS_IGNORED. Escape, the trigger that opened it and the
          header's close button are the other ways out. */}
      <Sheet
        open={sheetOpen}
        onOpenChange={handleSheetOpenChange}
        modal={false}
      >
        <SheetPopup
          showCloseButton={false}
          showBackdrop={false}
          initialFocus={fieldsPage ? fieldsSearchRef : undefined}
          surface="background"
          className={cn(
            frame === "card"
              ? `${CARD_PANEL_WIDTH} rounded-r-[calc(var(--radius-lg)-1px)]`
              : anchorId
                ? `${PANEL_WIDTH} rounded-br-[calc(var(--radius-md)-1px)]`
                : PANEL_WIDTH,
            snapClose && "duration-0",
          )}
          style={
            anchorInsets
              ? {
                  marginTop: anchorInsets.top,
                  marginRight: anchorInsets.right,
                  marginBottom: anchorInsets.bottom,
                }
              : undefined
          }
        >
          <Box
            direction="row"
            align="center"
            gap="sm"
            paddingX="lg"
            paddingY="md"
            shrink={false}
          >
            <Box direction="row" align="center" gap="xs" shrink fill="flex">
              {backButton}
              <SheetTitle className="text-base">{headerTitle}</SheetTitle>
            </Box>
            <Box direction="row" align="center" gap="xs" shrink={false}>
              {headerActions}
              <SheetClose
                aria-label="Close"
                render={<Button size="icon-xs" color="neutral" />}
              >
                <Icon name="x" />
              </SheetClose>
            </Box>
          </Box>
          {rungSlot}
          {hasHeaderRung ? null : <Separator />}
          {/* Keyed on the page: a new page opens at its top, not wherever the
              last one was scrolled to. */}
          <SheetPanel key={pageKey} className="p-0">
            {body}
          </SheetPanel>
        </SheetPopup>
      </Sheet>
      {discardDialog}
    </>
  );
}
